import React, { useState, useEffect, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CategoryRail from './CategoryRail';
import FeaturedHeroCarousel from './FeaturedHeroCarousel';
import LoadingSpinner from './common/LoadingSpinner';
import { HomepageRailsSkeleton } from './common/SkeletonLoaders';
import ErrorBoundary from './common/ErrorBoundary';
import DynamicMetaTags from './SEO/DynamicMetaTags';
import LoginCTASection from './PlaceholderRails/LoginCTASection';
import { useAppLoad } from '../contexts/AppLoadContext';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { useWatchlist } from '../hooks/useWatchlist';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { userDataService } from '../services/UserDataService';
import './Homepage.css';

const CategoriesError = ({ error, retry }) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-6">
    <div className="max-w-md text-center">
      <h2 className="mb-4 text-2xl font-bold text-white">Unable to Load Content</h2>
      <p className="mb-6 text-white/70">
        We&apos;re having trouble loading the streaming content. Please check your connection and
        try again.
      </p>
      <button
        onClick={retry}
        className="px-6 py-3 font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Try Again
      </button>
    </div>
  </div>
);

const Homepage = () => {
  const { categories, loading, error, retry } = useAppLoad();
  const { continueWatchingData, hasContinueWatching } = useContinueWatching();
  const { watchlistData, hasWatchlist } = useWatchlist();
  const { isAuthenticated } = useStreamAuth();
  const location = useLocation();

  const navigate = useNavigate();

  // Watch again from /v1/me/state (personal rails are not in /v1/home).
  const [watchAgain, setWatchAgain] = useState([]);
  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setWatchAgain([]);
      return undefined;
    }
    userDataService.refreshResumptionsData().then(data => {
      if (active) {
        setWatchAgain(data['watch-again'] || []);
      }
    });
    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  // `/?channel=<slug>` (old share links): the title's watch page.
  useEffect(() => {
    const channelSlug = new URLSearchParams(location.search).get('channel');
    if (channelSlug) {
      navigate(`/watch/${encodeURIComponent(channelSlug)}`, { replace: true });
    }
  }, [location.search, navigate]);

  // The programmed hero rail leads; with none, the first rail is the hero.
  const heroCategoryIndex = categories.findIndex(c => c.layout === 'hero');
  const resolvedHeroIndex = heroCategoryIndex >= 0 ? heroCategoryIndex : 0;
  const featuredCategory = categories[resolvedHeroIndex] || null;
  const regularCategories = categories.filter((_, idx) => idx !== resolvedHeroIndex);
  const getRailWrapperClass = idx =>
    idx === 0 ? 'homepage-first-rail -mt-72 md:-mt-80 xl:-mt-80 z-20 relative' : 'mb-8';

  // Check error before the loading/empty-content guard: a failed appload leaves
  // categories empty, so the guard below would otherwise mask the failure as an
  // infinite spinner and never reach the error + retry UI. fetchAppLoad clears
  // error at the start of every attempt, so a retry shows the spinner, not a
  // stale error.
  if (error) {
    return <CategoriesError error={error} retry={retry} />;
  }

  if (loading || (!featuredCategory && !categories?.length)) {
    return <LoadingSpinner message="Loading streaming content..." />;
  }

  return (
    <div className="min-h-screen bg-primary homepage" style={{ marginTop: '-72px' }}>
      <DynamicMetaTags />

      {/* Full-screen Hero Carousel - pulls up behind transparent header */}
      <ErrorBoundary fallback="Failed to load featured content">
        <Suspense fallback={<LoadingSpinner message="Loading featured content..." />}>
          {featuredCategory && <FeaturedHeroCarousel category={featuredCategory} />}
        </Suspense>
      </ErrorBoundary>

      {/* Content Rails - With overlapping effect */}
      <main className="container relative mx-auto pb-16">
        <ErrorBoundary fallback="Failed to load categories">
          <Suspense fallback={<HomepageRailsSkeleton railCount={3} />}>
            {regularCategories.length > 0 ? (
              regularCategories.map((category, idx) => {
                // Insert Continue Watching / Watchlist / Watch Again at idx 2
                if (idx === 2) {
                  return (
                    <React.Fragment key={`cw-block-${category._id}`}>
                      {/* Continue Watching Rail */}
                      {isAuthenticated && hasContinueWatching && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'continue-watching',
                              name: 'Continue Watching',
                              display_name: 'Continue Watching',
                              channels: continueWatchingData,
                              style: 'continue-watching',
                            }}
                          />
                        </div>
                      )}

                      {/* Watchlist Rail */}
                      {isAuthenticated && hasWatchlist && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'my-watchlist',
                              name: 'My List',
                              display_name: 'My List',
                              channels: watchlistData,
                            }}
                          />
                        </div>
                      )}

                      {/* Watch Again Rail */}
                      {isAuthenticated && watchAgain.length > 0 && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'watch-again',
                              name: 'Watch Again',
                              display_name: 'Watch Again',
                              channels: watchAgain,
                            }}
                          />
                        </div>
                      )}

                      {/* Continue Watching CTA for Non-Authenticated Users */}
                      {!isAuthenticated && (
                        <LoginCTASection
                          sectionTitle="Continue Watching"
                          title="Pick up right where you left off."
                          subtitle="Login now to continue watching your favorite series & movies."
                        />
                      )}

                      {/* Regular Category Rail */}
                      <div className={getRailWrapperClass(idx)}>
                        <CategoryRail category={category} forceTextTitle={idx === 0} />
                      </div>
                    </React.Fragment>
                  );
                }

                // Insert Watch Again CTA for non-authenticated users at index 4
                if (idx === 4 && !isAuthenticated) {
                  return (
                    <React.Fragment key={`wa-cta-${category._id}`}>
                      <LoginCTASection
                        sectionTitle="Watch Again"
                        title="Rediscover your favorites."
                        subtitle="Login now to watch again your favorite series & movies."
                      />
                      <div className="mb-8">
                        <CategoryRail category={category} />
                      </div>
                    </React.Fragment>
                  );
                }

                // Regular category rail
                return (
                  <div key={category._id} className={getRailWrapperClass(idx)}>
                    <CategoryRail category={category} forceTextTitle={idx === 0} />
                  </div>
                );
              })
            ) : (
              <HomepageRailsSkeleton railCount={5} />
            )}
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
};

export default Homepage;
